import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms/button'

export type ConfirmDialogOptions = {
  message?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  width?: number
  height?: number
}

export const useConfirmModal = () => {
  const { t } = useTranslation()
  const [isShowDialog, setIsShowDialog] = useState(false)
  const [dialogOptions, setDialogOptions] = useState<ConfirmDialogOptions>()
  const [onClickButton, setOnClickButton] = useState<(isConfirmed: boolean) => void>()

  const openConfirmDialog = (options?: ConfirmDialogOptions): Promise<boolean> => {
    setDialogOptions(options)
    setIsShowDialog(true)
    return new Promise(resolve => setOnClickButton(() => resolve))
  }

  const onClose = (isConfirmed: boolean) => {
    setIsShowDialog(false)
    setDialogOptions(undefined)
    if (onClickButton) {
      onClickButton(isConfirmed)
    }
  }

  const modalWidth = dialogOptions?.width ?? 400
  const modalHeight = dialogOptions?.height ?? 187

  const ConfirmDialog: React.FC = () =>
    isShowDialog ? (
      <>
        <div css={modalBackgroundStyle}> </div>
        <div css={modalStyle(modalWidth, modalHeight)}>
          <div className="text-area">
            {dialogOptions?.message ?? (
              <>
                <p>{t('The usage detail date is outside the stay period.')}</p>
                <p>{t('Do you want to register?')}</p>
              </>
            )}
          </div>
          <div className="button-area">
            <Button width={110} height={38} buttonType={3} fontSize={14} onClick={() => onClose(false)}>
              {dialogOptions?.cancelLabel ?? t('Cancel')}
            </Button>
            <Button width={110} height={38} buttonType={1} fontSize={14} onClick={() => onClose(true)}>
              {dialogOptions?.confirmLabel ?? t('Save')}
            </Button>
          </div>
        </div>
      </>
    ) : (
      <></>
    )
  return {
    openConfirmDialog,
    ConfirmDialog,
  }
}

const modalStyle = (width: number, height: number) =>
  css({
    height,
    width,
    position: 'fixed',
    zIndex: 1000,
    background: '#FFFFFF',
    top: `calc(-50vh + 100% - ${height}px / 2)`,
    left: `calc(-50vw + 100% - ${width}px / 2)`,
    padding: '0 40px',
    borderRadius: 5,
    boxShadow: '0px 0px 6px 0px rgba(0,0,0,0.1)',
    '.text-area': {
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: 18,
      color: '#272727',
      letterSpacing: 0.9,
      paddingTop: 40,
      lineHeight: '27px',
      whiteSpace: 'pre-line',
    },
    '.button-area': {
      display: 'flex',
      justifyContent: 'center',
      gap: 16,
      paddingTop: 24,
    },
  })

const modalBackgroundStyle = css({
  background: '#000000',
  width: '100vw',
  height: '100vh',
  position: 'fixed',
  zIndex: 999,
  top: 'calc(-100vh + 100%)',
  left: 'calc(-100vw + 100%)',
  opacity: 0.5,
})
